import { defineComponent, getCurrentInstance, ref, computed, toRefs, watch } from 'vue-demi';
import { D as DropdownClose, a as DropdownOpen } from './dropdown-open-06d651cf.js';
import { i as include, e as exclude } from './include-2a81e823.js';
import './dom-53c9635b.js';
import { i as isEmpty } from './isString-cf957a95.js';
import { t as toArray } from './toArray-93b84b21.js';
import { s as searchItemsByLabel } from './searchItemsByLabel-c778a1e4.js';
import './TimeFormat-a7f5565b.js';
import '@vueuse/core';
import { u as useProxyModelValue } from './useProxyModelValue-e18f251e.js';
import { u as useResponsive } from './useResponsive-491da8c6.js';
import '@onereach/styles/tailwind.config.json';
import { s as script$1 } from './OrCheckbox-e353ee89.js';
import { s as script$2 } from './OrError-331220bc.js';
import { s as script$3 } from './OrExpansionPanel-bb8883a1.js';
import { s as script$4 } from './OrHint-262c8217.js';
import { s as script$6 } from './OrIconButton-5a508869.js';
import { s as script$5 } from './OrIcon-9aff520e.js';
import { s as script$8, a as InputBoxVariant } from './OrInputBox-2f33498c.js';
import { s as script$7 } from './OrInput-6b60bf4a.js';
import { s as script$9 } from './OrLabel-4abbce6e.js';
import { s as script$a } from './OrMenuItem-6caf8844.js';
import { s as script$b } from './OrPopover-0daeba14.js';
import './OrSkeletonCircle-a3eb5f53.js';
import './OrSkeletonRect-d0b8b2cf.js';
import { s as script$e } from './OrSkeletonText-5e9579c1.js';
import { T as TagsOverflow } from './OrTags-44507e58.js';
import { s as script$c } from './OrSelectMultipleControl-274c9752.js';
import { s as script$d } from './OrSelectSingleControl-70644f38.js';
import './OrSelectPlaceholder-81502228.js';
import { resolveComponent, resolveDirective, openBlock, createElementBlock, normalizeClass, createBlock, withCtx, renderSlot, createTextVNode, toDisplayString, createCommentVNode, withDirectives, createElementVNode, withModifiers, createVNode, vShow, Fragment, renderList } from 'vue';

const Select = [
// Layout
'layout-column',
// Spacing
'gap-xs'];
const SelectControl = [
// Layout
'layout-row', 'w-full',
// Layout (empty)
'empty:hidden',
// Overflow
'overflow-hidden',
// Spacing
'gap-xs',
// Typography
'typography-inherit'];
const SelectSearchControl = [
// Layout
'grow',
// Spacing
'!mx-lg md:!-mx-sm+', '!my-sm md:!my-0'];
const SelectDropdown = [
// Overflow
'overflow-y-auto',
// Box
'max-h-[340px]',
// Theme
'theme-background-default', 'dark:theme-background-default-dark', 'theme-border-outline border-1', 'dark:theme-border-outline-dark'];
const SelectDropdownDefault = [
// Shape
'rounded-b-md',
// Theme
'!border-t-0'];
const SelectDropdownFlipped = [
// Shape
'rounded-t-md',
// Theme
'!border-b-0'];
const SelectDropdownItem = [
// Typography
'!typography-body-2-regular',
// Typography (selected)
'selected:!typography-body-2-semibold'];
const SelectNoSearchResults = [
// Layout
'layout-row justify-center',
// Spacing
'px-md', 'py-md',
// Typography
'typography-caption-regular',
// Theme
'theme-foreground-outline', 'dark:theme-foreground-outline-dark'];

var script = defineComponent({
  name: 'OrSelect',
  components: {
    OrCheckbox: script$1,
    OrError: script$2,
    OrExpansionPanel: script$3,
    OrHint: script$4,
    OrIcon: script$5,
    OrIconButton: script$6,
    OrInput: script$7,
    OrInputBox: script$8,
    OrLabel: script$9,
    OrMenuItem: script$a,
    OrPopover: script$b,
    OrSelectMultipleControl: script$c,
    OrSelectSingleControl: script$d,
    OrSkeletonText: script$e
  },
  directives: {
    DropdownClose,
    DropdownOpen
  },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue'
  },
  props: {
    modelValue: {
      type: undefined,
      default: undefined
    },
    options: {
      type: Array,
      default: () => []
    },
    searchOptions: {
      type: Array,
      default: () => []
    },
    multiple: {
      type: Boolean,
      default: false
    },
    overflow: {
      type: String,
      default: () => TagsOverflow.Hidden
    },
    variant: {
      type: String,
      default: undefined
    },
    size: {
      type: String,
      default: undefined
    },
    anchor: {
      type: Object,
      default: undefined
    },
    label: {
      type: String,
      default: undefined
    },
    placeholder: {
      type: String,
      default: undefined
    },
    hint: {
      type: String,
      default: undefined
    },
    error: {
      type: [String, Boolean],
      default: undefined
    },
    loading: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    enableSearch: {
      type: Boolean,
      default: false
    },
    enableClear: {
      type: Boolean,
      default: false
    },
    searchFunction: {
      type: Function,
      default: searchItemsByLabel
    },
    groupByFunction: {
      type: Function,
      default: undefined
    },
    externalControl: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'focus', 'blur', 'search'],
  expose: ['root', 'open', 'close', 'toggle', 'toggleOption'],
  setup(props, context) {
    var _a;
    const currentInstance = getCurrentInstance();
    // Refs
    const root = ref();
    const inputBox = ref();
    const inputBoxRoot = computed(() => {
      var _a;
      return (_a = inputBox.value) === null || _a === void 0 ? void 0 : _a.root;
    });
    const control = ref();
    const multipleControl = ref();
    const singleControl = ref();
    const searchControl = ref();
    const popover = ref();
    const popoverRoot = computed(() => {
      var _a;
      return (_a = popover.value) === null || _a === void 0 ? void 0 : _a.root;
    });
    const popoverState = computed(() => {
      var _a;
      return (_a = popover.value) === null || _a === void 0 ? void 0 : _a.state;
    });
    // Props
    const {
      enableClear,
      enableSearch,
      modelValue,
      multiple
    } = toRefs(props);
    // Styles
    const rootStyles = computed(() => ['or-select-v3', ...Select, ...(props.variant === InputBoxVariant.Embedded ? ['grow'] : [])]);
    const controlStyles = computed(() => [...SelectControl]);
    const searchControlStyles = computed(() => [...SelectSearchControl]);
    const dropdownStyles = computed(() => {
      var _a;
      return ['or-select-popover-v3', ...SelectDropdown, ...(((_a = popover.value) === null || _a === void 0 ? void 0 : _a.isFlipped) ? SelectDropdownFlipped : SelectDropdownDefault)];
    });
    const dropdownItemStyles = computed(() => [...SelectDropdownItem]);
    const noSearchResultsStyles = computed(() => [...SelectNoSearchResults]);
    // State
    const controlId = ref((_a = context.attrs.id) !== null && _a !== void 0 ? _a : currentInstance.uid.toString());
    const proxyModelValue = useProxyModelValue(modelValue, context.emit);
    const isEmptyModelValue = computed(() => {
      return isEmpty(proxyModelValue.value);
    });
    const showClear = computed(() => {
      return enableClear.value && !isEmptyModelValue.value;
    });
    const focused = computed(() => {
      var _a, _b;
      if (multiple.value) {
        return (_a = multipleControl.value) === null || _a === void 0 ? void 0 : _a.focused;
      } else {
        return (_b = singleControl.value) === null || _b === void 0 ? void 0 : _b.focused;
      }
    });
    const readonly = computed(() => context.attrs.readonly);
    const internalSearchOptions = ref(props.options);
    const resolvedSearchOptions = computed(() => {
      return props.externalControl ? props.searchOptions : internalSearchOptions.value;
    });
    const searchText = ref('');
    watch([() => props.externalControl, searchText, () => props.options], async ([externalControl, searchText, options]) => {
      if (externalControl) {
        context.emit('search', searchText);
      } else {
        internalSearchOptions.value = await props.searchFunction(searchText, options);
      }
    }, {
      deep: true
    });
    const groupedOptions = computed(() => {
      const groupBy = props.groupByFunction;
      if (groupBy) {
        return internalSearchOptions.value.reduce((accumulator, option) => {
          var _a;
          const group = groupBy(option);
          return {
            ...accumulator,
            [group]: [...((_a = accumulator[group]) !== null && _a !== void 0 ? _a : []), option]
          };
        }, {});
      }
      return {
        '': props.options
      };
    });
    // Methods
    function toggle() {
      if (!readonly.value && !props.disabled) {
        const popoverInstance = popover.value;
        if (popoverInstance) {
          if (popoverState.value === 'open' && !focused.value) {
            close();
          } else {
            open();
            setTimeout(() => {
              focus();
            });
          }
        }
      }
    }
    function open() {
      const popoverInstance = popover.value;
      if (popoverInstance) {
        popoverInstance.open();
      }
    }
    function close() {
      const popoverInstance = popover.value;
      if (popoverInstance) {
        popoverInstance.close();
      }
    }
    function focus() {
      if (enableSearch.value) {
        const controlInstance = isMobile.value ? searchControl.value : multiple.value ? multipleControl.value : singleControl.value;
        if (controlInstance) {
          controlInstance.focus();
        }
      }
    }
    function selectOption(value) {
      if (props.multiple) {
        proxyModelValue.value = include(value, toArray(proxyModelValue.value));
      } else {
        proxyModelValue.value = value;
      }
      searchText.value = '';
    }
    function deselectOption(value) {
      if (props.multiple) {
        proxyModelValue.value = exclude(value, toArray(proxyModelValue.value));
      } else {
        proxyModelValue.value = undefined;
      }
    }
    function toggleOption(value) {
      if (props.multiple) {
        const model = toArray(proxyModelValue.value);
        if (!model.includes(value)) {
          proxyModelValue.value = include(value, model);
        } else {
          proxyModelValue.value = exclude(value, model);
        }
      } else {
        proxyModelValue.value = proxyModelValue.value !== value ? value : undefined;
      }
      searchText.value = '';
    }
    // Utils
    const {
      isDesktop,
      isMobile
    } = useResponsive();
    return {
      root,
      inputBox,
      inputBoxRoot,
      control,
      multipleControl,
      singleControl,
      searchControl,
      popover,
      popoverRoot,
      popoverState,
      rootStyles,
      controlStyles,
      searchControlStyles,
      dropdownStyles,
      dropdownItemStyles,
      noSearchResultsStyles,
      controlId,
      proxyModelValue,
      readonly,
      isEmptyModelValue,
      showClear,
      searchText,
      internalSearchOptions,
      resolvedSearchOptions,
      groupedOptions,
      toggle,
      close,
      open,
      selectOption,
      deselectOption,
      toggleOption,
      isDesktop,
      isMobile
    };
  }
});

const _hoisted_1 = ["id", "disabled"];
const _hoisted_2 = {
  class: /*#__PURE__*/normalizeClass(['layout-inline-row', 'gap-sm'])
};
const _hoisted_3 = {
  key: 1,
  class: /*#__PURE__*/normalizeClass(['px-sm', 'py-xs'])
};
const _hoisted_4 = ["onClick"];
function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_OrLabel = resolveComponent("OrLabel");
  const _component_OrSelectMultipleControl = resolveComponent("OrSelectMultipleControl");
  const _component_OrSelectSingleControl = resolveComponent("OrSelectSingleControl");
  const _component_OrIconButton = resolveComponent("OrIconButton");
  const _component_OrIcon = resolveComponent("OrIcon");
  const _component_OrInput = resolveComponent("OrInput");
  const _component_OrSkeletonText = resolveComponent("OrSkeletonText");
  const _component_OrCheckbox = resolveComponent("OrCheckbox");
  const _component_OrMenuItem = resolveComponent("OrMenuItem");
  const _component_OrExpansionPanel = resolveComponent("OrExpansionPanel");
  const _component_OrPopover = resolveComponent("OrPopover");
  const _component_OrInputBox = resolveComponent("OrInputBox");
  const _component_OrHint = resolveComponent("OrHint");
  const _component_OrError = resolveComponent("OrError");
  const _directive_dropdown_open = resolveDirective("dropdown-open");
  const _directive_dropdown_close = resolveDirective("dropdown-close");
  return openBlock(), createElementBlock("div", {
    ref: 'root',
    class: normalizeClass(_ctx.rootStyles)
  }, [_ctx.$slots.label || _ctx.$slots.addon || _ctx.label ? (openBlock(), createBlock(_component_OrLabel, {
    key: 0,
    "control-id": _ctx.controlId,
    variant: 'input',
    required: _ctx.required,
    disabled: _ctx.disabled
  }, {
    addon: withCtx(() => [renderSlot(_ctx.$slots, "addon")]),
    default: withCtx(() => [renderSlot(_ctx.$slots, "label", {}, () => [createTextVNode(toDisplayString(_ctx.label), 1 /* TEXT */)])]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["control-id", "required", "disabled"])) : createCommentVNode("v-if", true), withDirectives((openBlock(), createBlock(_component_OrInputBox, {
    ref: 'inputBox',
    class: normalizeClass([_ctx.popoverState === 'open' && (_ctx.popover && _ctx.popover.isFlipped ? 'rounded-t-0' : 'rounded-b-0'), {
      'outline-none': _ctx.variant === 'embedded'
    }]),
    variant: _ctx.variant,
    size: _ctx.size,
    invalid: !!_ctx.error,
    readonly: _ctx.readonly,
    disabled: _ctx.disabled,
    tabindex: !_ctx.disabled ? 0 : null
  }, {
    default: withCtx(() => [createElementVNode("div", {
      id: _ctx.controlId,
      ref: 'control',
      class: normalizeClass(_ctx.controlStyles),
      disabled: _ctx.disabled,
      onFocus: _cache[3] || (_cache[3] = $event => _ctx.$emit('focus', $event)),
      onBlur: _cache[4] || (_cache[4] = $event => _ctx.$emit('blur', $event))
    }, [renderSlot(_ctx.$slots, "valueTemplate", {
      model: _ctx.proxyModelValue
    }, () => [_ctx.multiple ? (openBlock(), createBlock(_component_OrSelectMultipleControl, {
      key: 0,
      ref: 'multipleControl',
      modelValue: _ctx.searchText,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => _ctx.searchText = $event),
      model: _ctx.proxyModelValue,
      options: _ctx.options,
      overflow: _ctx.overflow,
      placeholder: _ctx.placeholder,
      readonly: _ctx.readonly,
      disabled: _ctx.disabled,
      "enable-search": _ctx.enableSearch,
      "is-open": _ctx.popoverState === 'open',
      onReset: _cache[1] || (_cache[1] = $event => _ctx.deselectOption($event))
    }, {
      tag: withCtx(({
        item
      }) => [renderSlot(_ctx.$slots, "tag", {
        item: item
      })]),
      _: 3 /* FORWARDED */
    }, 8 /* PROPS */, ["modelValue", "model", "options", "overflow", "placeholder", "readonly", "disabled", "enable-search", "is-open"])) : (openBlock(), createBlock(_component_OrSelectSingleControl, {
      key: 1,
      ref: 'singleControl',
      modelValue: _ctx.searchText,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = $event => _ctx.searchText = $event),
      model: _ctx.proxyModelValue,
      options: _ctx.options,
      placeholder: _ctx.placeholder,
      disabled: _ctx.disabled,
      "enable-search": _ctx.enableSearch,
      "is-open": _ctx.popoverState === 'open'
    }, null, 8 /* PROPS */, ["modelValue", "model", "options", "placeholder", "disabled", "enable-search", "is-open"]))])], 42 /* CLASS, PROPS, NEED_HYDRATION */, _hoisted_1), createElementVNode("div", _hoisted_2, [_ctx.showClear ? (openBlock(), createBlock(_component_OrIconButton, {
      key: 0,
      icon: 'close',
      color: 'inherit',
      "additional-styles": ['!text-outline', '!dark:text-outline-dark'],
      disabled: _ctx.readonly || _ctx.disabled,
      onClick: _cache[5] || (_cache[5] = withModifiers($event => _ctx.$emit('update:modelValue', _ctx.multiple ? [] : undefined), ["stop"]))
    }, null, 8 /* PROPS */, ["disabled"])) : createCommentVNode("v-if", true), withDirectives(createVNode(_component_OrIcon, {
      icon: _ctx.popoverState === 'open' ? _ctx.enableSearch ? 'search' : 'arrow_drop_up' : 'arrow_drop_down',
      class: normalizeClass(_ctx.popoverState === 'open' && _ctx.enableSearch ? '!text-outline !dark:text-outline-dark' : ''),
      variant: 'outlined'
    }, null, 8 /* PROPS */, ["icon", "class"]), [[vShow, !(_ctx.popoverState === 'open' && _ctx.enableSearch && _ctx.enableClear && !_ctx.isEmptyModelValue)]])]), createVNode(_component_OrPopover, {
      ref: 'popover',
      class: normalizeClass(_ctx.dropdownStyles),
      "custom-styles": _ctx.dropdownStyles,
      variant: 'auto',
      trigger: _ctx.anchor || _ctx.inputBoxRoot,
      placement: 'bottom',
      offset: 0,
      "full-width": true,
      "disable-default-styles": true
    }, {
      default: withCtx(() => [_ctx.popoverState === 'open' && _ctx.enableSearch && _ctx.isMobile ? (openBlock(), createBlock(_component_OrInput, {
        key: 0,
        ref: 'searchControl',
        modelValue: _ctx.searchText,
        "onUpdate:modelValue": _cache[6] || (_cache[6] = $event => _ctx.searchText = $event),
        class: normalizeClass(_ctx.searchControlStyles),
        type: 'search',
        placeholder: _ctx.placeholder
      }, null, 8 /* PROPS */, ["modelValue", "class", "placeholder"])) : createCommentVNode("v-if", true), _ctx.loading ? (openBlock(), createElementBlock("div", _hoisted_3, [renderSlot(_ctx.$slots, "loading", {}, () => [createVNode(_component_OrSkeletonText, {
        "font-size": 28,
        "line-height": 36,
        "lines-count": 9
      })])])) : _ctx.resolvedSearchOptions.length > 0 ? (openBlock(), createElementBlock(Fragment, {
        key: 2
      }, [_ctx.groupByFunction ? (openBlock(true), createElementBlock(Fragment, {
        key: 0
      }, renderList(_ctx.groupedOptions, (group, groupName) => {
        return openBlock(), createBlock(_component_OrExpansionPanel, {
          key: groupName,
          label: groupName
        }, {
          default: withCtx(() => [_ctx.multiple ? (openBlock(true), createElementBlock(Fragment, {
            key: 0
          }, renderList(group, (option, i) => {
            return openBlock(), createBlock(_component_OrMenuItem, {
              key: i,
              selected: _ctx.proxyModelValue && _ctx.proxyModelValue.includes(option.value),
              disabled: option.disabled
            }, {
              default: withCtx(() => [renderSlot(_ctx.$slots, "optionTemplate", {
                model: _ctx.proxyModelValue,
                option: option
              }, () => [createVNode(_component_OrCheckbox, {
                modelValue: _ctx.proxyModelValue,
                "onUpdate:modelValue": _cache[7] || (_cache[7] = $event => _ctx.proxyModelValue = $event),
                value: option.value,
                label: option.label,
                "label-variant": 'menu-item',
                disabled: option.disabled
              }, null, 8 /* PROPS */, ["modelValue", "value", "label", "disabled"])])]),
              _: 2 /* DYNAMIC */
            }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["selected", "disabled"]);
          }), 128 /* KEYED_FRAGMENT */)) : (openBlock(true), createElementBlock(Fragment, {
            key: 1
          }, renderList(group, (option, i) => {
            return openBlock(), createBlock(_component_OrMenuItem, {
              key: i,
              class: normalizeClass(_ctx.dropdownItemStyles),
              selected: _ctx.proxyModelValue === option.value,
              disabled: option.disabled,
              onClick: withModifiers($event => _ctx.selectOption(option.value), ["stop"])
            }, {
              default: withCtx(() => [renderSlot(_ctx.$slots, "optionTemplate", {
                model: _ctx.proxyModelValue,
                option: option
              }, () => [createTextVNode(toDisplayString(option.label), 1 /* TEXT */)])]),
              _: 2 /* DYNAMIC */
            }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["class", "selected", "disabled", "onClick"]);
          }), 128 /* KEYED_FRAGMENT */))]),
          _: 2 /* DYNAMIC */
        }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["label"]);
      }), 128 /* KEYED_FRAGMENT */)) : (openBlock(), createElementBlock(Fragment, {
        key: 1
      }, [_ctx.multiple ? (openBlock(true), createElementBlock(Fragment, {
        key: 0
      }, renderList(_ctx.resolvedSearchOptions, (option, i) => {
        return openBlock(), createBlock(_component_OrMenuItem, {
          key: i,
          selected: _ctx.proxyModelValue && _ctx.proxyModelValue.includes(option.value),
          disabled: option.disabled,
          onClick: withModifiers($event => !option.disabled && _ctx.toggleOption(option.value), ["stop"])
        }, {
          default: withCtx(() => [createVNode(_component_OrCheckbox, {
            "model-value": _ctx.proxyModelValue,
            value: option.value,
            "label-variant": 'menu-item',
            disabled: option.disabled
          }, {
            default: withCtx(() => [createElementVNode("div", {
              onClick: $event => !option.disabled && _ctx.toggleOption(option.value)
            }, [renderSlot(_ctx.$slots, "optionTemplate", {
              model: _ctx.proxyModelValue,
              option: option
            }, () => [createTextVNode(toDisplayString(option.label), 1 /* TEXT */)])], 8 /* PROPS */, _hoisted_4)]),
            _: 2 /* DYNAMIC */
          }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["model-value", "value", "disabled"])]),
          _: 2 /* DYNAMIC */
        }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["selected", "disabled", "onClick"]);
      }), 128 /* KEYED_FRAGMENT */)) : (openBlock(true), createElementBlock(Fragment, {
        key: 1
      }, renderList(_ctx.resolvedSearchOptions, (option, i) => {
        return openBlock(), createBlock(_component_OrMenuItem, {
          key: i,
          class: normalizeClass(_ctx.dropdownItemStyles),
          selected: _ctx.proxyModelValue === option.value,
          disabled: option.disabled,
          onClick: withModifiers($event => {
            _ctx.selectOption(option.value);
            _ctx.close();
          }, ["stop"])
        }, {
          default: withCtx(() => [renderSlot(_ctx.$slots, "optionTemplate", {
            model: _ctx.proxyModelValue,
            option: option
          }, () => [createTextVNode(toDisplayString(option.label), 1 /* TEXT */)])]),
          _: 2 /* DYNAMIC */
        }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["class", "selected", "disabled", "onClick"]);
      }), 128 /* KEYED_FRAGMENT */))], 64 /* STABLE_FRAGMENT */))], 64 /* STABLE_FRAGMENT */)) : (openBlock(), createElementBlock("div", {
        key: 3,
        class: normalizeClass(_ctx.noSearchResultsStyles)
      }, [renderSlot(_ctx.$slots, "noSearchResultsTemplate", {
        searchText: _ctx.searchText
      }, () => [createTextVNode(" No results ")])], 2 /* CLASS */))]),
      _: 3 /* FORWARDED */
    }, 8 /* PROPS */, ["class", "custom-styles", "trigger"])]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["class", "variant", "size", "invalid", "readonly", "disabled", "tabindex"])), [[_directive_dropdown_open, _ctx.toggle], [_directive_dropdown_close, _ctx.close]]), _ctx.$slots.hint || _ctx.hint ? withDirectives((openBlock(), createBlock(_component_OrHint, {
    key: 1,
    disabled: _ctx.disabled
  }, {
    default: withCtx(() => [renderSlot(_ctx.$slots, "hint", {}, () => [createTextVNode(toDisplayString(_ctx.hint), 1 /* TEXT */)])]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["disabled"])), [[vShow, !_ctx.error]]) : createCommentVNode("v-if", true), withDirectives(createVNode(_component_OrError, {
    disabled: _ctx.disabled
  }, {
    default: withCtx(() => [createTextVNode(toDisplayString(_ctx.error), 1 /* TEXT */)]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["disabled"]), [[vShow, typeof _ctx.error === 'string' && _ctx.error.length > 0]])], 2 /* CLASS */);
}

script.render = render;
script.__file = "src/components/or-select-v3/OrSelect.vue";

export { script as s };
